import { t } from '../i18n';
// admin-client/src/components/AgentAssetControl.jsx
'use client';
import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
/* ───────────────────────────────────────────
 * 공통: ESC 닫힘
 * ─────────────────────────────────────────── */
function useCloseOnEsc(handler, enabled = true) {
    const handlerRef = React.useRef(handler);
    React.useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);
    React.useEffect(() => {
        if (!enabled) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') handlerRef.current?.();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [enabled]);
}
/* ───────────────────────────────────────────
 * 모달 쉘 (반응형 폭)
 * ─────────────────────────────────────────── */
function ModalShell({ open, onClose, children, className = 'w-[360px] max-w-[92vw]' }) {
    useCloseOnEsc(onClose, open);
    if (!open)
        return null;
    return createPortal((<div className="admin-user-modal-portal fixed inset-0 z-40 flex items-start sm:items-center justify-center overflow-y-auto p-4 sm:p-6" role="presentation">
      <div className="fixed inset-0 bg-black/30" onClick={onClose}/>
      <div className={`relative w-full max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white rounded-2xl p-4 sm:p-5 shadow-xl ${className}`}>
        {children}
      </div>
    </div>), document.body);
}
/* ───────────────────────────────────────────
 * 숫자 입력 모달 (모바일 친화)
 * ─────────────────────────────────────────── */
function NumberInputModal({ open, title, placeholder = '0.00', confirmText = t("\uC801\uC6A9"), onApply, onClose }) {
    const [val, setVal] = React.useState('');
    React.useEffect(() => { if (open)
        setVal(''); }, [open]);
    return (<ModalShell open={open} onClose={onClose}>
      <div className="text-base font-semibold mb-2">{title}</div>
      <input type="number" inputMode="decimal" min="0.01" max="1000000000" step="0.01" className="w-full border rounded-lg px-3 py-3 text-right text-base sm:text-sm" placeholder={placeholder} value={val} onChange={(e) => setVal(e.target.value)}/>
      <div className="mt-1 text-[11px] text-gray-500 text-right">{t("\uC18C\uC218 \uB458\uC9F8 \uC790\uB9AC\uAE4C\uC9C0 \uC785\uB825 \uAC00\uB2A5")}</div>
      <div className="mt-4 flex justify-end gap-2">
        <button className="px-3 py-2 rounded-lg border" onClick={onClose}>{t("\uCDE8\uC18C")}</button>
        <button className="px-3 py-2 rounded-lg bg-gray-900 text-white" onClick={() => onApply(String(val || '').trim())}>
          {confirmText}
        </button>
      </div>
    </ModalShell>);
}
/* ───────────────────────────────────────────
 * AgentAssetControl
 *  - API 호출 없음: 부모 콜백(onCredit/onDebit)에 위임
 *  - seedAsset으로 표시, 상위에서 patch 후 재렌더
 *  - 모바일에서 라벨 축약/숨김, 버튼/여백/폰트 반응형
 * ─────────────────────────────────────────── */
const AgentAssetControl = React.forwardRef(function AgentAssetControl({ targetId, seedAsset = 0, onCredit, onDebit, className = '', label = t("\uC790\uC0B0"), }, actionRef) {
    // 표시 전용 로컬 상태 (부모 seedAsset 변화에 동기화)
    const [asset, setAsset] = React.useState(Number(seedAsset) || 0);
    const [openCredit, setOpenCredit] = React.useState(false);
    const [openDebit, setOpenDebit] = React.useState(false);
    const [working, setWorking] = React.useState(false);
    // 모바일 유저 목록의 '더보기' 메뉴가 기존 자산 모달을 열도록 제공.
    // 입력 검증과 실제 지급/차감 로직은 기존 컴포넌트 하나에서만 처리한다.
    React.useImperativeHandle(actionRef, () => ({
        openCredit: () => { if (!working) setOpenCredit(true); },
        openDebit: () => { if (!working) setOpenDebit(true); },
    }), [working]);
    React.useEffect(() => {
        setAsset(Number(seedAsset) || 0);
    }, [seedAsset]);
    const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '-');
    const normalizeInputAmount = (raw) => {
        const text = String(raw ?? '').trim();
        if (!/^\d+(?:\.\d{1,2})?$/.test(text)) return null;
        const n = Number(text);
        if (!Number.isFinite(n) || n < 0.01 || n > 1000000000) return null;
        return text;
    };
    const handleCredit = async (amount) => {
        const normalizedAmount = normalizeInputAmount(amount);
        if (!normalizedAmount)
            return alert(t("0.01 ~ 1,000,000,000.00 \uBC94\uC704\uC758 \uAE08\uC561\uC744 \uC18C\uC218 \uB458\uC9F8 \uC790\uB9AC\uAE4C\uC9C0 \uC785\uB825\uD558\uC138\uC694."));
        if (typeof onCredit !== 'function')
            return alert(t("onCredit \uCF5C\uBC31\uC774 \uC5F0\uACB0\uB418\uC5B4 \uC788\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."));
        setOpenCredit(false);
        try {
            setWorking(true);
            await onCredit(targetId, normalizedAmount);
        }
        finally {
            setWorking(false);
        }
        // 부모가 트리 패치 → seedAsset 갱신되면 위 useEffect로 화면 반영됨
    };
    const handleDebit = async (amount) => {
        const normalizedAmount = normalizeInputAmount(amount);
        if (!normalizedAmount)
            return alert(t("0.01 ~ 1,000,000,000.00 \uBC94\uC704\uC758 \uAE08\uC561\uC744 \uC18C\uC218 \uB458\uC9F8 \uC790\uB9AC\uAE4C\uC9C0 \uC785\uB825\uD558\uC138\uC694."));
        if (typeof onDebit !== 'function')
            return alert(t("onDebit \uCF5C\uBC31\uC774 \uC5F0\uACB0\uB418\uC5B4 \uC788\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."));
        setOpenDebit(false);
        try {
            setWorking(true);
            await onDebit(targetId, normalizedAmount);
        }
        finally {
            setWorking(false);
        }
        // 부모가 트리 패치 → seedAsset 갱신되면 위 useEffect로 화면 반영됨
    };
    return (<div className={[
            // 모바일에서 줄바꿈 허용 → 버튼이 자연스럽게 다음 줄로 이동
            'flex items-center gap-1 sm:gap-2 flex-wrap sm:flex-nowrap',
            // 모바일에서 가로폭을 꽉 채워주면 카드 내부 레이아웃이 안정적
            'w-full sm:w-auto',
            className,
        ].join(' ')} aria-busy={working ? 'true' : 'false'}>
      {/* 금액 뱃지: 모바일에서 라벨 숨김, 숫자만 */}
      <div className="text-[12px] sm:text-[12px] px-2 py-1 rounded-lg border bg-white whitespace-nowrap" title={`${label}: ${fmt(asset)}`}>
        <span className="text-gray-500 mr-1 hidden sm:inline">{label}</span>
        <b className="tabular-nums" aria-live="polite">{fmt(asset)}</b>
      </div>

      {/* 버튼 그룹: 모바일에서도 눌리기 쉽게 높이 확보 / 텍스트는 그대로 유지 */}
      <button className="px-2 py-2 sm:py-1 text-[12px] sm:text-[12px] rounded border bg-gray-50 hover:bg-gray-100 disabled:opacity-50" onClick={() => setOpenCredit(true)} disabled={working} title={t("\uC9C0\uAE09")}>{t("\uC9C0\uAE09")}</button>
      <button className="px-2 py-2 sm:py-1 text-[12px] sm:text-[12px] rounded border bg-gray-50 hover:bg-gray-100 disabled:opacity-50" onClick={() => setOpenDebit(true)} disabled={working} title={t("\uCC28\uAC10")}>{t("\uCC28\uAC10")}</button>

      {/* 모달들 */}
      <NumberInputModal open={openCredit} title={t('지급 (대상: {targetId})', { targetId })} onApply={handleCredit} onClose={() => setOpenCredit(false)}/>
      <NumberInputModal open={openDebit} title={t('차감 (대상: {targetId})', { targetId })} onApply={handleDebit} onClose={() => setOpenDebit(false)}/>
    </div>);
});
AgentAssetControl.propTypes = {
    targetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    seedAsset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onCredit: PropTypes.func, // (targetId, amount) => Promise<void>
    onDebit: PropTypes.func, // (targetId, amount) => Promise<void>
    className: PropTypes.string,
    label: PropTypes.string,
};

export default AgentAssetControl;
